import { useGetOrders } from '@vindr/services/actions/order.action';
import { STATUS_COLOR, STATUS_PRIORITY_COLOR } from '@vindr/services/constants/common.constant';
import {
  ORDER_COLUMNS_ORIGINAL,
  ORDER_PRIORITY,
  ORDER_STATUS,
} from '@vindr/services/constants/order.constant';
import { DefinedColumns } from '@vindr/services/interfaces';
import ColumnTemplate from '@vindr/ui/src/components/Templates/columns/columnRenderTemplates';
import { formatDateColumn } from '@vindr/utils/dateTime';
import { Pick, getArrayVal } from '@vindr/utils/helpers/common.helper';
import { localStorageAction } from '@vindr/utils/helpers/storage';
import { Tag, Tooltip } from 'antd';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

interface OrderColumnsType
  extends DefinedColumns<ReturnType<typeof useGetOrders>['data']['data'][number]> {}

const defaultColumns = [
  'index',
  'priority',
  'status',
  'patient_id',
  'patient_name',
  'order_date',
  'study_desc',
  'patient_arrived_time',
  'tech_notes',
  'study_department',
  'started_at',
  'completed_at',
  'approved_at',
  'delivered_at',
];

const useDefinedColumns = ({ pagination }): OrderColumnsType[] => {
  const { t } = useTranslation('Vindoc');

  const definedColumns = [
    ColumnTemplate.Index({
      pagination,
      key: 'index',
      dataIndex: 'index',
      width: 60,
    }),
    {
      title: t('Priority'),
      dataIndex: 'priority',
      key: 'priority',
      width: 80,
      ellipsis: true,
      render: (priority) => (
        <Tag
          color={STATUS_PRIORITY_COLOR[ORDER_PRIORITY[priority]]}
          className="flex w-full justify-center"
        >
          {t(ORDER_PRIORITY[priority])}
        </Tag>
      ),
    },
    {
      title: t('Status'),
      width: 110,
      dataIndex: 'status',
      key: 'status',
      ellipsis: true,
      render: (status) => (
        <Tag color={STATUS_COLOR[ORDER_STATUS[status]]} className="flex w-full justify-center">
          {t(ORDER_STATUS[status])}
        </Tag>
      ),
    },
    {
      title: t('Order Group'),
      dataIndex: 'order_group',
      key: 'order_group',
      width: 110,
      sorter: true,
      ellipsis: true,
    },
    {
      title: t('Patient ID'),
      width: 110,
      dataIndex: 'patient_id',
      key: 'patient_id',
      sorter: true,
      ellipsis: true,
      render: (_, record) => record.visited_patient?.patient_id,
    },
    {
      title: t('Patient Name'),
      width: 200,
      dataIndex: 'patient_name',
      key: 'patient_name',
      sorter: true,
      ellipsis: true,
      render: (_, record) => record.visited_patient?.name,
    },
    {
      title: t('Order Date'),
      dataIndex: 'order_date',
      key: 'order_date',
      width: 140,
      ellipsis: true,
      render: (date) => formatDateColumn(date),
    },
    {
      title: t('Scheduled Date'),
      dataIndex: 'scheduled_date',
      key: 'scheduled_date',
      width: 140,
      sorter: true,
      ellipsis: true,
      defaultSortOrder: 'descend',
      render: (date) => formatDateColumn(date),
    },
    {
      title: t('Scheduled By'),
      dataIndex: 'scheduled_by_name',
      render: (_, record) => record.scheduled_by_name || record.scheduled_by_username,
      key: 'scheduled_by_name',
      width: 120,
      ellipsis: true,
    },
    {
      title: t('Study Description'),
      dataIndex: 'study_desc',
      key: 'study_desc',
      ellipsis: true,
      width: 300,
      render: (_, record) => {
        return record.procedure?.name || '';
      },
    },
    {
      title: t('Modality'),
      dataIndex: 'modality',
      key: 'modality',
      width: 80,
      render: (_, record) => record.procedure?.modality,
    },
    {
      title: t('Device'),
      dataIndex: 'device_name',
      key: 'device_name',
      width: 100,
      ellipsis: true,
      render: (_, record) => record.device?.name,
    },
    {
      title: t('Suspended At'),
      dataIndex: 'suspended_at',
      key: 'suspended_at',
      width: 140,
      ellipsis: true,
      render: (date) => formatDateColumn(date),
    },
    {
      title: t('Suspended By'),
      dataIndex: 'suspended_by_name',
      render: (_, record) => record.suspended_by_name || record.suspended_by_username,
      key: 'suspended_by_name',
      width: 120,
      ellipsis: true,
    },
    {
      title: t('Canceled At'),
      dataIndex: 'cancelled_at',
      key: 'cancelled_at',
      width: 140,
      ellipsis: true,
      render: (date) => formatDateColumn(date),
    },
    {
      title: t('Canceled By'),
      dataIndex: 'cancelled_by_name',
      render: (_, record) => record.cancelled_by_name || record.cancelled_by_username,
      key: 'cancelled_by_name',
      width: 120,
      ellipsis: true,
    },
    {
      title: t('Received At'),
      dataIndex: 'patient_arrived_time',
      key: 'patient_arrived_time',
      width: 140,
      sorter: true,
      ellipsis: true,
      render: (date) => formatDateColumn(date),
    },
    {
      title: t('Received By'),
      dataIndex: 'received_by_name',
      render: (_, record) => record.received_by_name || record.received_by_username,
      key: 'received_by_name',
      width: 120,
      ellipsis: true,
    },
    {
      title: t('Note'),
      dataIndex: 'tech_notes',
      key: 'tech_notes',
      width: 200,
      ellipsis: true,
      render: (tech_notes) => (
        <Tooltip
          title={tech_notes?.map((note, index) => (
            <div key={index}>{String(`- ${note?.text}`)}</div>
          ))}
          placement="topLeft"
        >
          <span title="">{tech_notes?.map((note) => `${String(note?.text)}`)?.join(', ')}</span>
        </Tooltip>
      ),
    },
    {
      title: t('Request Department'),
      dataIndex: ['department', 'name'],
      key: 'study_department',
      width: 180,
      ellipsis: true,
    },
    {
      title: t('Started At'),
      dataIndex: 'started_at',
      key: 'started_at',
      width: 140,
      ellipsis: true,
      render: (date) => formatDateColumn(date),
    },
    {
      title: t('Started By'),
      dataIndex: 'started_by_name',
      render: (_, record) => record.started_by_name || record.started_by_username,
      key: 'started_by_name',
      width: 120,
      ellipsis: true,
    },
    {
      title: t('Completed At'),
      dataIndex: 'completed_at',
      key: 'completed_at',
      width: 140,
      ellipsis: true,
      render: (date) => formatDateColumn(date),
    },
    {
      title: t('Completed By'),
      dataIndex: 'completed_by_name',
      render: (_, record) => record.completed_by_name || record.completed_by_username,
      key: 'completed_by_name',
      width: 120,
      ellipsis: true,
    },
    {
      title: t('Approved At'),
      dataIndex: 'approved_at',
      key: 'approved_at',
      width: 140,
      ellipsis: true,
      render: (date) => formatDateColumn(date),
    },
    {
      title: t('Approved By'),
      dataIndex: 'approved_by',
      render: (_, record) => record?.approved_by_name || record?.approved_by_username,
      key: 'approved_by_name',
      width: 120,
      ellipsis: true,
    },
    {
      title: t('Delivered At'),
      dataIndex: 'delivered_at',
      key: 'delivered_at',
      width: 140,
      ellipsis: true,
      render: (date) => formatDateColumn(date),
    },
    {
      title: t('Delivered By'),
      dataIndex: 'delivered_by_name',
      render: (_, record) => record.delivered_by_name || record.delivered_by_username,
      key: 'delivered_by_name',
      width: 120,
      ellipsis: true,
    },
    {
      title: t('Result Delivery Location'),
      dataIndex: 'result_delivery_location',
      key: 'result_delivery_location',
      width: 180,
      ellipsis: true,
    },
    {
      title: t('Ordinal Number'),
      dataIndex: 'ordinal_number',
      key: 'ordinal_number',
      width: 110,
      ellipsis: true,
    },
    {
      title: t('Visit ID'),
      width: 100,
      dataIndex: 'visit_id',
      key: 'visited_patient.visit_id',
      sorter: true,
      ellipsis: true,
      render: (_, record) => record.visited_patient?.visit_id,
    },
    {
      title: t('Institution'),
      dataIndex: 'institution',
      key: 'institution.name',
      width: 180,
      ellipsis: true,
      render: (_, record) => record.institution?.name,
    },
    {
      title: t('Order Doctor Name'),
      dataIndex: 'order_doctor_name',
      key: 'order_doctor_name',
      width: 80,
      ellipsis: true,
      render: (text) => getArrayVal(text),
    },
    {
      title: t('Referring Physician'),
      dataIndex: 'refer_doctor_name',
      key: 'refer_doctor_name',
      width: 80,
      ellipsis: true,
      render: (text) => getArrayVal(text),
    },
    ColumnTemplate.WaitingTime({
      dataIndex: 'patient_arrived_time',
      endIndex: 'started_at',
      resultIndex: 'approved_at',
      sorter: true,
      title: t('Wait Time'),

      detailed: true,
      unit: 'minute',
      requirement: 'optional',
      interval: true,
    }),

    ColumnTemplate.ElapsedTime({
      dataIndex: 'patient_arrived_time',
      endIndex: 'approved_at',
      key: 'total_time',
      sorter: true,
      title: t('Total Time'),

      detailed: true,
      unit: 'minute',
      requirement: 'all',
      interval: true,
    }),
  ];

  const sorted = definedColumns.sort((a, b) => {
    const indexA = defaultColumns.indexOf(a.key);
    const indexB = defaultColumns.indexOf(b.key);

    if (indexA === -1) return 1;
    if (indexB === -1) return -1;
    return indexA - indexB;
  });

  const columnDefault = [];

  for (const col of defaultColumns) {
    const column = definedColumns.find((item) => item.key === col);
    if (column) columnDefault.push(Pick(column, ['title', 'key', 'dataIndex', 'width']));
  }
  localStorageAction.set(ORDER_COLUMNS_ORIGINAL, { resizableColumns: columnDefault });

  return useMemo(() => sorted, [t]);
};

export default useDefinedColumns;
